/* ── Tour Room (Guest): the pure parts ────────────────────────────────
   Built to docs/product specs/tour_room_guest/. Everything here turns
   backend payloads into what the panel renders, with no React and no
   network, so the mapping can be tested against real shapes.

   THE RULE THIS FILE EXISTS TO ENFORCE, from the spec:
   "The names in the reference file are placeholders; the counts in copy
   must be derived." The reference HTML is full of confident numbers —
   12.4 acres, 7 towers, 41 floors, All 29 amenities — belonging to a
   project nobody is looking at. Every one of them has to come from data
   or not be shown at all. A buyer reading invented facts about a home
   they are considering is the worst failure this screen has.

   Field names verified against a live /api/v1/public/projects/{id}
   payload on 2026-08-20. */

export type Highlight = { value: string; unit?: string; label: string; note?: string };
export type UnitRow = { key: string; label: string; count: number | null; facing?: string };
export type AmenityGroup = { category: string; label: string; items: string[] };

/* Indian-format grouping. 75000 -> "75,000". Intl would do this, but it
   is locale-sensitive and this must not silently become "75.000" for a
   buyer whose browser is set to a European locale. */
export const groupIndian = (n: number): string => {
  const s = String(Math.round(n));
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}`;
};

/* "2026-08-30" -> "August 2026". Possession is a market-facing promise
   measured in months, never days: a precise-looking date implies a
   guarantee nobody in this industry makes. Returns "" for anything
   unparseable so the caller can drop the cell rather than print
   "Invalid Date". */
export const possessionLabel = (iso?: string | null): string => {
  const s = String(iso || "").trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleString("en-IN", { month: "long" })} ${d.getFullYear()}`;
};

/* The address, shortest useful form first. Locality then city is how
   people name a place out loud; the full postal address belongs on a
   document, not in a header line. */
export const locationLabel = (address: any): string => {
  const a = address || {};
  const parts = [a.landmark, a.city].map((x: any) => String(x || "").trim()).filter(Boolean);
  if (parts.length) return Array.from(new Set(parts)).join(", ");
  return String(a.province || a.country || "").trim();
};

/* The configuration line: "4BHK Villa", "2 · 3 · 4 BHK Apartment".
   Derived from the unit configurations, so a project that has none says
   nothing rather than guessing a product type. */
export const configurationLabel = (details: any): string => {
  const type = String(details?.type || "").replace(/_/g, " ").trim();
  const noun = /villa/i.test(type) ? "Villa"
    : /apartment|flat/i.test(type) ? "Apartment"
      : /plot/i.test(type) ? "Plot"
        : /commercial|office/i.test(type) ? "Commercial"
          : "";
  const bhks = Array.from(new Set(
    (details?.unit_configurations || [])
      .map((u: any) => Number(u?.bhk))
      .filter((n: number) => Number.isFinite(n) && n > 0),
  )).sort((a, b) => (a as number) - (b as number));
  if (!bhks.length) return noun;
  return `${bhks.join(" · ")} BHK${noun ? ` ${noun}` : ""}`;
};

/* The backend's ProjectType enum, as it actually is:
     apartment · villa_gated_community · layout_gated_community
     farmland · commercial
   Read off the same field configurationLabel uses, so the header and the
   highlights can never describe one project two ways. */
export const isApartmentProject = (p: any): boolean =>
  /apartment|flat/i.test(String(p?.project_type_details?.type || "").replace(/_/g, " "));

/* What to call the homes. "345 Villas" reads as a place; "345 Residences"
   reads as a spreadsheet — and on a plotted development neither is true. */
export const homesLabel = (p: any): string => {
  const type = String(p?.project_type_details?.type || "").replace(/_/g, " ");
  if (/apartment|flat/i.test(type)) return "Residences";
  if (/villa/i.test(type)) return "Villas";
  if (/layout|plot/i.test(type)) return "Plots";
  if (/farm/i.test(type)) return "Farm plots";
  return "Units";
};

/* The highlights ledger — FOUR facts for a villa community, SIX for an
   apartment one.

   ── WHY THE COUNT DIFFERS ──
   Not a layout preference. The four are the facts every gated community
   has: how much land, how many homes, how many amenities, how big the
   clubhouse. The extra two exist only when you build upward — towers and
   floors are meaningless for villas, and a villa project asked for six
   cells was always going to leave holes in the grid, which is exactly
   what it did.

   ── WHAT WAS DROPPED, AND WHY ──
   Saleable area. It is a developer's total and a RERA figure, not
   something a buyer weighs: nobody chooses a home because the project
   sells 1,00,000 sqft in aggregate. The number a buyer actually wants —
   the size of the unit they are looking at — is in the configurations
   list directly below this, per unit, where it means something.

   ── STILL NOT INVENTED ──
   Towers and floors are emitted only when the project data carries them.
   No field does today, so an apartment project renders four cells until
   the backend supplies `towers` / `floors`. A cell that cannot be filled
   from data is dropped, never defaulted to a plausible number — a made-up
   floor count in front of a buyer is worse than an absent one. */
export const highlightsFrom = (p: any): Highlight[] => {
  const out: Highlight[] = [];
  const details = p?.project_type_details || {};

  const area = p?.project_area;
  if (area?.value) {
    out.push({ value: String(area.value), unit: String(area.unit || "").toUpperCase(), label: "Land parcel" });
  }

  /* Apartments only, and immediately after the land parcel because the two
     read together: 22 acres across 6 towers is a different place from 22
     acres across 2.

     Field names taken from the backend's ApartmentProjectType, where both
     are required (`gt=0`) — so a real apartment project always has them,
     and an absent value means something is wrong with the record rather
     than that this project has no towers. Still guarded: a missing cell is
     better than a zero presented as a fact. */
  if (isApartmentProject(p)) {
    const towers = Number(details.number_of_towers) || 0;
    if (towers > 0) {
      out.push({ value: String(towers), label: towers === 1 ? "Tower" : "Towers" });
    }
    const floors = Number(details.number_of_floors) || 0;
    if (floors > 0) {
      out.push({ value: String(floors), label: "Floors", note: "per tower" });
    }
  }

  const units = details.unit_configurations || [];
  const residences = units.reduce((n: number, u: any) => n + (Number(u?.count) || 0), 0);
  if (residences > 0) {
    const bhks = Array.from(new Set(units.map((u: any) => Number(u?.bhk)).filter(Boolean)))
      .sort((a, b) => (a as number) - (b as number));
    out.push({
      value: groupIndian(residences),
      label: homesLabel(p),
      note: bhks.length ? `${bhks.join(" · ")} BHK` : undefined,
    });
  }

  const amenities = (p?.amenities || []).filter((a: any) => a?.value !== false);
  if (amenities.length) {
    const cats = new Set(amenities.map((a: any) => String(a?.category || "").trim()).filter(Boolean));
    out.push({
      value: String(amenities.length),
      label: "Amenities",
      note: cats.size ? `across ${cats.size} ${cats.size === 1 ? "group" : "groups"}` : undefined,
    });
  }

  const club = Number(details.clubhouse_size_sqft) || 0;
  if (club > 0) out.push({ value: groupIndian(club), unit: "SF", label: "Clubhouse" });

  return out;
};

/* A still image of where the property is.

   ── WHY A STATIC IMAGE AND NOT AN EMBED ──
   The obvious move is Google's `output=embed` iframe, which the Expand
   overlay uses. It plants Google's own red marker in the middle, and there
   is no way to remove or restyle it from outside the frame — a red pin in
   a panel built entirely from greens reads as an error state. An iframe
   also swallows scroll and drag inside a panel the buyer is scrolling.

   A static image has neither problem, costs one request, and lets the room
   place its OWN marker over it — which is what the design always intended:
   the placeholder this replaced was an emerald dot with a glow.

   So NO `markers=` parameter. The image is centred on the coordinates and
   the dot is drawn in CSS on top, dead centre.

   Returns "" when there is no key or no coordinates, and the panel falls
   back to the abstract grid rather than a broken image. */
export const staticMapUrl = (
  geo: [number, number] | number[] | null | undefined,
  opts: { key?: string; mapId?: string; zoom?: number; width?: number; height?: number } = {},
): string => {
  const key = String(opts.key || "").trim();
  if (!key || !Array.isArray(geo) || geo.length !== 2) return "";
  const [lat, lng] = geo;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return "";

  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: String(opts.zoom ?? 15),
    /* scale=2 because this sits on phones, where a 1x tile is visibly soft
       and a map a buyer cannot read is the same as no map. */
    size: `${opts.width ?? 640}x${opts.height ?? 340}`,
    scale: "2",
    key,
  });
  /* The agency's own cloud style, when one is configured — the same map id
     the analytics and project pages already use, so the room doesn't
     introduce a second visual language for maps. */
  if (opts.mapId) params.set("map_id", String(opts.mapId));
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

/* How many columns the highlights grid should use for a given number of
   cells. Four in a three-column grid leaves one orphan on its own row;
   five leaves a visible hole. Neither is a data problem — the grid was
   simply fixed at three. */
export const highlightColumns = (count: number): number => {
  if (count <= 1) return 1;
  if (count === 2 || count === 4) return 2;
  return 3;
};

/* One row per configuration. `count` may legitimately be absent — some
   projects publish sizes without inventory — and null renders as no
   pill rather than as a confident "0 available". */
export const unitRowsFrom = (p: any): UnitRow[] =>
  (p?.project_type_details?.unit_configurations || []).map((u: any, i: number) => {
    const name = String(u?.name || (u?.bhk ? `${u.bhk} BHK` : "")).trim();
    const size = Number(u?.size_sqft) || 0;
    const count = Number.isFinite(Number(u?.count)) && u?.count !== null ? Number(u.count) : null;
    return {
      key: `${name || "unit"}-${i}`,
      label: size ? `${name} · ${groupIndian(size)} sqft` : name,
      count,
      facing: u?.facing ? String(u.facing) : undefined,
    };
  }).filter((r: UnitRow) => !!r.label);

const CATEGORY_LABELS: Record<string, string> = {
  home: "In the home",
  commercial: "Commercial",
  community: "Community",
  wellness: "Wellness & fitness",
  outdoor: "Outdoors",
  safety: "Utility & safety",
};

const titleCase = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* The categorised directory the "All N" link opens. Groups come from the
   data's own `category`, so a project with three categories shows three
   — the design's six is a property of its sample, not a layout rule. */
export const amenityGroupsFrom = (p: any): AmenityGroup[] => {
  const live = (p?.amenities || []).filter((a: any) => a?.value !== false && a?.name);
  const byCat = new Map<string, string[]>();
  live.forEach((a: any) => {
    const cat = String(a.category || "other").trim().toLowerCase();
    if (!byCat.has(cat)) byCat.set(cat, []);
    (byCat.get(cat) as string[]).push(String(a.name));
  });
  return Array.from(byCat.entries())
    .map(([category, items]) => ({
      category,
      label: CATEGORY_LABELS[category] || titleCase(category),
      items: items.sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => b.items.length - a.items.length);
};

export const amenityCount = (p: any): number =>
  (p?.amenities || []).filter((a: any) => a?.value !== false && a?.name).length;

/* ── Family pass quota ────────────────────────────────────────────────
   ONE link with four places, not four links — see the spec's §5. The
   cap is enforced server-side; this is only how it reads.

   `used` is clamped into range because it comes off a server counter we
   do not own: a negative would render a broken bar and an overshoot
   would render more segments than the design has. */
export const familyQuota = (used: number, max = 4) => {
  const u = Math.max(0, Math.min(max, Math.floor(Number(used) || 0)));
  return {
    used: u,
    /* Echoed back so callers render the SAME number the quota was
       computed against. Two places used to hardcode 4 while the bar read
       the backend value, so raising the cap would have produced "2 of 6"
       above "All 4 places are taken". */
    max,
    remaining: max - u,
    exhausted: u >= max,
    segments: Array.from({ length: max }, (_, i) => i < u),
    label: `${u} of ${max}`,
  };
};

/* The note under the family link. Reads as a fact, never as an error —
   a household that used all four places did nothing wrong. */
export const familyNote = (used: number, max = 4): string =>
  familyQuota(used, max).exhausted
    ? `All ${max} places on your family link are used.`
    : `One link, up to ${max} people. Each enters their own name — nothing else.`;

/* ── People ──────────────────────────────────────────────────────────
   A guest sees a flat list with no controls. The tag is the whole
   information design here: it answers "who else is in the room with me",
   which for a buyer is a trust question, not a roster.

   `invited_by` is what distinguishes family from any other guest: it
   carries the id of whoever issued the pass. Comparing it to our own
   participant id is how we know a guest came in on OUR link rather than
   somebody else's. */
export type PersonRow = {
  session_id: string;
  /* ── WHAT AGORA CALLS THIS PERSON ──
     The speaking ring is keyed on the Agora uid; this list is keyed on the
     session id. Those are the same string ONLY while
     GUIDED_RTC_UID_MODE=account, which is what stage runs today. Under
     `int` the uid becomes a number, every lookup misses, and every
     talking ring goes dark — silently, on the same deploy that turns
     recording on, which is a good way to spend an afternoon blaming the
     wrong change.

     The backend now returns `rtc_uid` on the roster (item 25, shipped
     2026-09-11) precisely so a client can bridge the two. Carried here
     and preferred by the UI, so the flip is a no-op for us. Falls back to
     the session id, which is correct today and harmless after. */
  rtc_uid: string;
  name: string;
  tag: "" | "Host" | "You" | "Family";
  sub: string;
  avatar: "em" | "sky" | "warm" | "lil";
};

export const peopleRows = (
  admitted: any[],
  selfSessionId: string,
  opts: { hostName?: string; selfName?: string; selfMobile?: string; agencyName?: string } = {},
): PersonRow[] =>
  (admitted || []).map((p: any) => {
    const role = String(p?.role || "").toLowerCase();
    const isSelf = String(p?.session_id) === String(selfSessionId);
    const name = String(p?.display_name || "").trim()
      || (role === "host" ? String(opts.hostName || "Your advisor") : "")
      || (isSelf ? String(opts.selfName || "You") : "Guest");

    if (isSelf) {
      return {
        session_id: String(p?.session_id),
        rtc_uid: String(p?.rtc_uid || p?.session_id || ""),
        name,
        tag: "You" as const,
        sub: String(opts.selfMobile || ""),
        avatar: "sky" as const,
      };
    }
    if (role === "host") {
      return {
        session_id: String(p?.session_id),
        rtc_uid: String(p?.rtc_uid || p?.session_id || ""),
        name,
        tag: "Host" as const,
        sub: opts.agencyName ? `Hosting · ${opts.agencyName}` : "Hosting this tour",
        avatar: "em" as const,
      };
    }
    if (role === "guest") {
      const mine = p?.invited_by && String(p.invited_by) === String(selfSessionId);
      return {
        session_id: String(p?.session_id),
        rtc_uid: String(p?.rtc_uid || p?.session_id || ""),
        name,
        tag: "Family" as const,
        sub: mine && opts.selfName ? `Family of ${opts.selfName}` : "Family",
        avatar: "warm" as const,
      };
    }
    /* ── EVERYONE ELSE IS JUST A NAME ──
       This branch used to label the row "Agent · <agency>", which was wrong
       twice over.

       Wrong as a fact: the presence role is only ever host | lead | guest, so
       a fellow BUYER and a colleague of the advisor arrive here identically.
       Most rows that land in this branch are other buyers, and calling them
       agents misdescribes the room to the person reading it.

       Wrong as a disclosure even when it happened to be right: invited lead,
       uninvited lead, new lead, staff — that is the agency's internal sales
       vocabulary, and it exists to help a host decide who to admit. A buyer
       being told that the other person on their tour is an "uninvited lead"
       has been shown a page of someone else's CRM.

       So: the name, and nothing else. The host's screen keeps the full
       classification, which is where it belongs. */
    return {
      session_id: String(p?.session_id),
      rtc_uid: String(p?.rtc_uid || p?.session_id || ""),
      name,
      tag: "" as const,
      sub: "",
      avatar: "lil" as const,
    };
  });

/* ── THE ROSTER A BUYER CAN ACTUALLY SEE ──
   peopleRows() above renders the lobby, and the lobby is host-only:
   GET views/guided_room/{id}/lobby carries require("virtual_tour","read")
   AND _assert_host(), so a buyer — who has no Keycloak account — gets a
   401, `admitted` stays [], and the People tab said "Just you so far"
   while the header two inches away named the advisor. The page was
   contradicting itself.

   Two people are known WITHOUT the lobby: the host, whose first name
   comes from the public join preview, and the buyer, who is holding the
   credential they joined with. Those two rows are facts, so show them.

   Family and other buyers are NOT knowable here, and we claim nothing
   about them — that needs a credential-authenticated roster read on the
   backend, the same auth the chat write and rtc_token already use.

   Live rows win. When the lobby IS readable — a host testing their own
   room while logged in — its rows are the truth and no synthetic row is
   added, so nothing ever appears twice.

   One known cost: the synthetic host row carries no real session id, so
   the speaking indicator can't light it up while they talk. A row that
   never glows beats no row at all. */
export const HOST_ROW_ID = "host";
export const SELF_ROW_ID = "you";

export const knownRoster = (
  rows: PersonRow[],
  selfSessionId: string,
  opts: { hostName?: string; selfName?: string; selfMobile?: string; agencyName?: string } = {},
): PersonRow[] => {
  const out = [...(rows || [])];
  const missing = (tag: PersonRow["tag"]) => !out.some((r) => r.tag === tag);

  if (missing("Host")) {
    out.push({
      session_id: HOST_ROW_ID,
      rtc_uid: HOST_ROW_ID,
      name: String(opts.hostName || "").trim() || "Your advisor",
      tag: "Host",
      sub: opts.agencyName ? `Hosting · ${opts.agencyName}` : "Hosting this tour",
      avatar: "em",
    });
  }
  if (missing("You")) {
    out.push({
      session_id: String(selfSessionId || "").trim() || SELF_ROW_ID,
      rtc_uid: String(selfSessionId || "").trim() || SELF_ROW_ID,
      name: String(opts.selfName || "").trim() || "You",
      tag: "You",
      sub: String(opts.selfMobile || ""),
      avatar: "sky",
    });
  }

  /* Host, then you, then everyone else — the order a buyer reads the
     room in. Array.prototype.sort is stable, so the lobby's own order
     survives inside the third group. */
  const rank = (r: PersonRow) => (r.tag === "Host" ? 0 : r.tag === "You" ? 1 : 2);
  return out.sort((a, b) => rank(a) - rank(b));
};

export const initialsOf = (name: string): string => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* mm:ss from the room's started_at. Guards a missing or future start —
   a negative timer counting up from -00:03 because two machines disagree
   about the clock is a needless thing for a buyer to see. */
export const elapsedLabel = (startedAt?: string | null, nowMs = Date.now()): string => {
  const s = String(startedAt || "").trim();
  if (!s) return "";
  const t = Date.parse(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isNaN(t)) return "";
  const secs = Math.max(0, Math.floor((nowMs - t) / 1000));
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

/* ── Who may hand out a family pass ────────────────────────────────────
   Locked 2026-08-23. The full admission matrix:

     Invited lead            admit: auto     family pass: YES
     Family of invited lead  admit: auto     family pass: no
     Uninvited lead          admit: gated    family pass: no
     New lead                admit: gated    family pass: no
     Invited staff           admit: auto     family pass: no
     Uninvited staff         admit: reject   family pass: no

   The rule behind it: a family pass BYPASSES THE LOBBY. That is its whole
   purpose — a buyer's household should not queue for approval — but
   skipping the host's gate is a privilege the host extends to someone
   they chose. An uninvited lead is by definition someone they didn't, so
   letting them mint four auto-admitted seats hands an unvetted arrival
   the power to fill the room. And a guest may never mint one, or a single
   link becomes unbounded: guests inviting guests inviting guests.

   Everyone else still has a route in — the visit link, which costs them
   an OTP and the host two clicks in the lobby. Friction in the right
   place rather than a door left open.

   ── FAILS CLOSED ──
   Returns true only on POSITIVE evidence of an invited lead. An unknown
   or unreadable role is treated as "not allowed", because the failure
   modes are not symmetrical: wrongly hiding the card costs a buyer one
   awkward question to their advisor, wrongly showing it puts strangers in
   a live tour.

   ── NOT A SECURITY BOUNDARY ──
   This hides a button. The backend must enforce the same rule on
   guest_pass — anyone can call that endpoint with a credential and a
   fetch. See docs/product specs/GUIDED_ROOM_ADMISSION_MATRIX.md. */
export const canIssueFamilyPass = (
  selfRow: any,
  room?: any,
  /* What the buyer learned about THEMSELVES when they verified. See
     admittedOnVerify() — this is the only one of the three signals that
     survives a buyer having no read access to the room. */
  credential?: { invited?: boolean; role?: string } | null,
): boolean => {
  /* ── WHY A THIRD SIGNAL ─────────────────────────────────────────────
     Tested live on 2026-09-07: an invited lead saw no family-pass card at
     all. Both of the signals below come from endpoints a buyer cannot
     read — `views/guided_room/{id}` and its `/lobby` are staff-only, so
     `selfRow` is undefined and `room` is null, and this returned false at
     the first line. The matrix was implemented correctly and the data
     never arrived.

     `credential.invited` is recorded at verification, from the one fact
     the backend tells a buyer about their own standing: verify_otp
     returns ADMITTED for a lead on the invite list and WAITING for
     everyone else (routers.py — `lobby = "ADMITTED" if is_invited else
     "WAITING"`). A guest arriving on a pass is WAITING too, and is
     excluded by role regardless.

     Ordering is deliberate: a live row still wins when we have one, since
     it reflects the room as it is now rather than as it was at the door. */
  const credRole = String(credential?.role || "").toLowerCase();
  if (credRole === "guest" || credRole === "staff" || credRole === "host") return false;

  if (!selfRow) return credential?.invited === true;

  const coarse = String(selfRow.role || "").toLowerCase();
  /* Guests and staff are excluded outright, whatever else is true. */
  if (coarse === "guest" || coarse === "staff" || coarse === "host") return false;

  /* The fine-grained source, when the lobby row carries it. This is the
     authoritative answer where present. */
  const fine = String(
    selfRow.source || selfRow.role_detail || selfRow.lead_kind || "",
  ).toLowerCase().replace(/[\s-]/g, "_");
  if (fine) {
    if (fine.includes("uninvited") || fine.includes("forward")) return false;
    if (fine.includes("new") || fine.includes("fresh") || fine.includes("captur")) return false;
    if (fine.includes("invited")) return true;
  }

  /* No `source` on the row: fall back to the room's own invite list, the
     same list the host page reads. Matching on any id the two sides might
     agree on, since which one identifies a lead has moved before. */
  const invitees: any[] = Array.isArray(room?.invitees)
    ? room.invitees
    : Array.isArray(room?.settings?.invitees)
      ? room.settings.invitees
      : [];
  /* Nothing in the room to prove it with — fall back to what they were
     told at the door.

     This line returned a bare false until 8 September, and that was fine
     only while a buyer had no roster row at all: the `!selfRow` branch
     above caught them first and read the credential. The moment the People
     tab started polling the participant roster, buyers DID have a row —
     one that is deliberately PII-stripped, so it carries no `source` — and
     every invited lead fell through the `fine` block to here and lost
     their family-pass card again. Found on a live tour within the hour, by
     which time the roster fix had made the pass fix silently useless. */
  if (!invitees.length) return credential?.invited === true;

  const mine = new Set(
    [selfRow.lead_id, selfRow.customer_id, selfRow.id, selfRow.session_id]
      .filter(Boolean).map((v: any) => String(v)),
  );
  if (!mine.size) return credential?.invited === true;
  return invitees.some((i: any) => {
    const id = String(i?.id ?? i?.lead_id ?? i ?? "");
    return !!id && mine.has(id);
  }) || credential?.invited === true;
};

/* Was this participant admitted the moment they verified?
   The backend admits an invited lead straight away and parks everyone
   else in the lobby, so a bare ADMITTED in the verify response is the
   buyer's own proof they were on the invite list. Read once, at the door,
   and carried in the credential — the room API that would otherwise
   answer this is staff-only.

   Anything unrecognised is false: this grants a capability, so it fails
   closed, and the backend enforces the same rule when the pass is
   actually issued. */
export const admittedOnVerify = (res: any): boolean =>
  String(res?.status ?? res?.lobby_status ?? "").toUpperCase() === "ADMITTED"
  || res?.auto_admitted === true;
